import React from "react";
import { overlayBase, useParticles } from "./_shared";

interface Props {
  /** Number of balloons, default 6 */
  count?: number;
  /** Array of balloon colours */
  colors?: string[];
  /** Base rise duration seconds, default 8 */
  dur?: number;
}

/**
 * BalloonFloat — floating balloon shapes with strings, drifting upward.
 * Great for party, celebration, or event-themed banners.
 */
const BalloonFloat: React.FC<Props> = ({
  count = 6,
  colors = ["#ff6b6b", "#4cc9f0", "#ffd166", "#a78bfa"],
  dur = 8,
}) => {
  const parts = useParticles(
    count,
    (i, r) => ({
      id: i,
      left: 8 + r(0) * 84,
      size: 20 + r(1) * 14,
      delay: r(2) * dur,
      d: dur * (0.8 + r(3) * 0.6),
      sway: 12 + r(4) * 12,
      color: colors[i % colors.length],
    }),
    [count, colors.join(","), dur]
  );

  return (
    <div style={overlayBase}>
      <style>{`
        @keyframes oot-balloon-rise {
          0%   { transform: translateY(20px); opacity: 0; }
          12%  { opacity: 0.9; }
          100% { transform: translateY(-115vh); opacity: 0.8; }
        }
        @keyframes oot-balloon-sway {
          0%, 100% { margin-left: calc(var(--sway) * -1); }
          50%       { margin-left: var(--sway); }
        }
      `}</style>
      {parts.map((p) => (
        <div
          key={p.id}
          style={{
            position: "absolute",
            left: `${p.left}%`,
            bottom: 0,
            animation: `oot-balloon-rise ${p.d}s ease-in ${p.delay}s infinite`,
          }}
        >
          <div
            style={{
              animation: `oot-balloon-sway ${p.d / 3}s ease-in-out infinite`,
              "--sway": `${p.sway}px`,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            } as React.CSSProperties}
          >
            {/* Balloon body */}
            <div
              style={{
                width: p.size,
                height: p.size * 1.25,
                borderRadius: "50% 50% 50% 50% / 55% 55% 45% 45%",
                background: `radial-gradient(circle at 30% 25%, rgba(255,255,255,0.6), ${p.color})`,
              }}
            />
            {/* String */}
            <div
              style={{ width: 1, height: 22, background: "rgba(255,255,255,0.5)" }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

export default BalloonFloat;
